<template>
  <div v-if="loading" class="grid place-items-center bg-detail-bg" :style="{ height: 'calc(100vh - 85px)' }">
      <Spinner :text="$t('common:text-loading')" />
  </div>
  <ContentUnavailable v-else-if="!variant?.id" class="m-6 lg:m-14 xl:m-16" />
  <div v-else>
    <div class="bg-detail-bg">
      <TopContentMenu />
      <ContentDetails :variant="variant" />
    </div>
  </div>
</template>

<script setup>
import _ from 'lodash-es';
import { ROUTES } from "@utils/routes";
import { API_ENDPOINTS } from "@utils/api/endpoints";
import { sitePages } from "@settings/site-pages.settings";
import { useAuthStore } from '@stores/auth';
import Spinner from "@components/ui/loaders/spinner/spinner";
import ContentDetails from "@components/content/content-details";
import ContentUnavailable from "@components/content/content-details/content-unavailable";
import TopContentMenu from "@components/category/top-content-menu";

defineI18nRoute({
  paths: {
    cs: '/inspirace/[slug]',
    en: '/inspiration/[slug]',
    sk: '/inspiracia/[slug]',
    pl: '/inspiracja/[slug]',
    de: '/inspiration/[slug]'
  }
});

const config = useRuntimeConfig();
const loading = ref(true);
const route = useRoute();
const { t } = useI18n();
const { $eventBus, $toast } = useNuxtApp();
const auth = useAuthStore();
const routeLang = useRouteLang();
const activityChecker = useActivityChecker();

const id = parseInt(route.params.slug);

const variant = ref({});
const setVariant = (data) => {
  _.assignIn(variant.value, data);

  const title = plainText(localized(data, 'title', localized(data, 'name')));
  const description = plainText(localized(data, 'description', localized(data, 'content')));
  const canonical = config.public.appBaseUrl + translatePath(ROUTES.CODE.INSPIRATION, slugify(data?.id, data?.name));

  useHead({ link: [ { rel: 'canonical', href: canonical } ] });
  useSeoMeta({
    title: title,
    ogTitle: title,
    description: description,
    ogDescription: description,
    ogImage: _.get(data, 'image.url', _.get(data, 'images.0.url', `${config.public.appBaseUrl}${sitePages[config.public.layoutDesign].image}`)),
    ogType: `${sitePages[config.public.layoutDesign].inspiration[0].metaType}`,
    ogUrl: canonical
  });
}

loading.value = true;
if (_.isInteger(id)) {
  const { data, error, status } = await useApiFetch(API_ENDPOINTS.CONTENT_POST_ID, {
    method: 'GET',
    path: { id },
    params: {
      locale: routeLang.locale.value
    }
  });
  if (status.value === 'error') {
    if (import.meta.client && !auth.reloadTimeout) {
      $toast.error(t('error.content-detail.load'));
    } else {
      sentryCaptureEvent({
        message: 'inspiration/[slug].',
        level: 'error',
        extra: { error: error.value }
      });
    }
  } else {
    setVariant(data.value);
  }
  loading.value = false;
} else {
  await navigatorTo(translatePath(ROUTES.CODE.INSPIRATION));
}

onServerPrefetch(async () => {
  if (!variant.value?.id) {
    throw createError({
      statusCode: 404,
      message: t('text-inspiration-unavailable-title'),
      statusMessage: t('text-inspiration-unavailable-subtitle')
    });
  }
});

const loggedInLoad = async (isLoggedIn) => _.delay(async () => {
  if (isLoggedIn && !variant.value?.isInCustomerFavorites && !variant.value?.isInCustomerLikes && !variant.value?.isInVisitorLikes) {
    _.assignIn(variant.value, _.pick(await contentPostLoad(id) || {}, ['isInCustomerFavorites', 'isInCustomerLikes', 'isInVisitorLikes']));
  }
}, 200);

watch(() => auth.isLoggedIn, loggedInLoad);
watch(() => activityChecker.wasUserActive.value, loggedInLoad);

const contentUpdate = (value) => {
  if (value.id === variant.value.id) {
    variant.value = { ...variant.value, ...value };
  }
};

onMounted(async () => {
  nextTick(async () => await loggedInLoad(auth.isLoggedIn || activityChecker.wasUserActive.value));
  $eventBus.on('content:update', contentUpdate);
});

onUnmounted(() => {
  $eventBus.off('content:update', contentUpdate);
});

</script>
